import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Award, Bookmark, Eye, EyeOff, MessageCircle, Repeat2 } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useReadOnScroll } from '../../../hooks/useReadOnScroll'
import { GalleryCarousel, VideoBlock } from './RedditMedia'
import { cn } from '../../../lib/utils'

// RedditCard — the rich-media variant of the article card for Reddit
// sources. Layout above the existing ArticleCard's row form: a top
// row with subreddit avatar + name + sub count + author + flair,
// title, then a media block (image / gallery carousel / inline video
// / link preview), then post flair + crosspost badge, then the
// engagement footer (upvotes ↑ + comments 💬 + awards 🏆), then the
// top-comment preview when present.
//
// The card stays a `<Link to=/phoenix/news/:id>` like the other
// cards — the gallery carousel buttons stop propagation so swiping
// doesn't navigate.
const RedditCard = React.memo(function RedditCard({
  article, locale, isSaved, isRead, onToggleRead, onAutoRead, view,
  // `from` tags which list this card was rendered in — used by the
  // /article/:id href so middle-click / Cmd-click opens in a new tab
  // with the right back-target. Plain click goes through
  // onOpenArticle so the modal opens without unmounting the list.
  from = 'news',
  onOpenArticle,
}) {
  const { t } = useTranslation()
  const articleDir = articleIsArabic(article.title) ? 'rtl' : 'ltr'
  const source = article.source || {}
  // Auto-mark-as-read on scroll-past with dwell — wires the outer
  // wrapper ref through useReadOnScroll so the same trigger logic
  // covers Reddit cards alongside the regular ArticleCard.
  const rootRef = useRef(null)
  useReadOnScroll(rootRef, article.id, onAutoRead, isRead)
  const upvotes = article.media_view_count
  const awards = article.media_like_count
  const comments = article.comment_count
  const crosspost = article.crosspost_origin
  const isVideo = article.media_kind === 'video'
  const isGallery = article.media_kind === 'gallery'

  const handleCardClick = (e) => {
    if (e.defaultPrevented) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
    e.preventDefault()
    onOpenArticle?.(article.id)
  }
  return (
    <a
      href={`/article/${article.id}?from=${from}`}
      onClick={handleCardClick}
      className="block"
      ref={rootRef}
      // See ArticleCard for the rationale. Reddit cards are taller +
      // more variable (embedded media, top-comment preview) so the
      // intrinsic-size hint sits at the average rather than the floor.
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 320px' }}
    >
      <Card
        className={cn(
          'hover:border-app-primary-500/40 transition-colors overflow-hidden',
          isRead && view === 'read' && 'opacity-60',
        )}
      >
        <CardContent className="p-3 sm:p-4 space-y-2.5">
          {/* Subreddit + author row. Wraps on overflow so a long
              author flair or subreddit name can't push the time +
              bookmark off the row entirely. The avatar / source name
              cluster stays on the first line; the flair drops to a
              second line if it doesn't fit. */}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap" dir="auto">
            {source.avatar_url ? (
              <img
                src={source.avatar_url.replace(/&amp;/g, '&')}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="w-5 h-5 rounded-full object-cover bg-muted flex-shrink-0"
              />
            ) : (
              <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-600 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                r/
              </span>
            )}
            <span className="font-semibold text-foreground/80 truncate max-w-[40%]">
              {source.name || ''}
            </span>
            {typeof source.subscriber_count === 'number' && (
              <span className="font-mono text-[10px]" dir="ltr">
                {formatCount(source.subscriber_count)}
              </span>
            )}
            {article.author && (
              <>
                <span>·</span>
                <span className="font-medium truncate max-w-[30%]" dir="ltr">u/{article.author}</span>
              </>
            )}
            {article.author_flair && (
              <FlairChip text={article.author_flair} tone="muted" />
            )}
            <span className="ms-auto" />
            <span dir="ltr" className="font-mono text-[10px] flex-shrink-0">
              {timeAgo(article.published_at, locale)}
            </span>
            {isSaved && (
              <Bookmark className="w-3.5 h-3.5 text-app-primary-500 fill-current flex-shrink-0" />
            )}
          </div>

          {/* Title */}
          <p
            className={cn(
              'text-sm text-foreground leading-snug',
              isRead ? 'font-normal' : 'font-semibold',
            )}
            dir={articleDir}
          >
            {article.title}
          </p>

          {/* Crosspost badge — under the title so it explains where
              the content came from before the media renders. Capped
              width + truncation guard against very-long subreddit
              names breaking the row width. */}
          {crosspost && (
            <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 max-w-full" dir="auto">
              <Repeat2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                {t('news.reddit.crosspostFrom', { sub: `r/${crosspost.subreddit}` })}
              </span>
            </div>
          )}

          {/* Post flair */}
          {article.post_flair && (
            <div className="inline-block">
              <FlairChip text={article.post_flair} tone="primary" />
            </div>
          )}

          {/* Body preview for self posts. Capped at 3 lines so the
              card height stays predictable. */}
          {article.summary && !isVideo && !isGallery && (
            <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed" dir={articleDir}>
              {article.summary}
            </p>
          )}

          {/* Media block — drives the visual mass of the card. */}
          {isGallery && Array.isArray(article.media_images) && article.media_images.length > 0 ? (
            <GalleryCarousel images={article.media_images} title={article.title} />
          ) : isVideo && article.media_video_url ? (
            <VideoBlock videoUrl={article.media_video_url} thumbnail={article.image_url} title={article.title} t={t} />
          ) : article.image_url ? (
            <ImageBlock url={article.image_url} title={article.title} />
          ) : null}

          {/* Top comment preview — the gold of Reddit. Styled like a
              callout quote so it stands apart from the post body. */}
          {article.top_comment_body && (
            <div
              className="rounded-md border border-input bg-muted/40 p-2.5 text-xs"
              dir={articleIsArabic(article.top_comment_body) ? 'rtl' : 'ltr'}
            >
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
                <MessageCircle className="w-3 h-3" />
                <span className="font-medium" dir="ltr">u/{article.top_comment_author}</span>
                {typeof article.top_comment_score === 'number' && (
                  <span className="font-mono" dir="ltr">↑{formatCount(article.top_comment_score)}</span>
                )}
                <span className="ms-1 text-[9px] uppercase tracking-wide text-app-primary-500 font-semibold">
                  {t('news.reddit.topComment')}
                </span>
              </div>
              <p className="text-foreground/80 line-clamp-3 leading-relaxed">
                {article.top_comment_body}
              </p>
            </div>
          )}

          {/* Engagement footer */}
          <div className="flex items-center gap-3 pt-1 text-[11px] text-muted-foreground" dir="ltr">
            {typeof upvotes === 'number' && (
              <span className="inline-flex items-center gap-1 font-medium">
                <ArrowUp className="w-3.5 h-3.5 text-orange-500" />
                <span className="font-mono">{formatCount(upvotes)}</span>
              </span>
            )}
            {typeof comments === 'number' && (
              <span className="inline-flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" />
                <span className="font-mono">{formatCount(comments)}</span>
              </span>
            )}
            {typeof awards === 'number' && awards > 0 && (
              <span className="inline-flex items-center gap-1">
                <Award className="w-3.5 h-3.5 text-yellow-500" />
                <span className="font-mono">{awards}</span>
              </span>
            )}
            {onToggleRead && (
              <ReadToggle
                isRead={isRead}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onToggleRead(article.id)
                }}
                t={t}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </a>
  )
})

// Single-image block. Constrained by max-h so a portrait image
// doesn't push the card height out of control.
function ImageBlock({ url, title }) {
  const [failed, setFailed] = useState(false)
  if (failed) return null
  return (
    <img
      src={(url || '').replace(/&amp;/g, '&')}
      alt={title || ''}
      // Lazy now that the list cold-mounts on every back-nav (sibling
      // /article/:id route). The old eager mode was a workaround for
      // the overlay architecture where cards stayed in the DOM but
      // re-decoded on overlay close — no longer applies.
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="w-full max-h-[420px] object-cover rounded-md bg-muted"
    />
  )
}

function FlairChip({ text, tone }) {
  // Subreddits set arbitrary flair text — sometimes a single emoji,
  // sometimes a full sentence. Cap width + truncate so an essay-
  // length flair can't push the avatar / username out of the row.
  return (
    <span
      className={cn(
        'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium max-w-[12rem] truncate align-middle',
        tone === 'primary'
          ? 'bg-app-primary-500/10 text-app-primary-500'
          : 'bg-muted text-muted-foreground',
      )}
      dir="auto"
      title={text}
    >
      {text}
    </span>
  )
}

// Mirrors the news ArticleCard read toggle — Eye / EyeOff icon
// rather than a localised word. Compact + consistent with the news
// page, which is what the user expects after toggling the feature
// kind from the source-kind chip row.
function ReadToggle({ isRead, onClick, t }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isRead}
      aria-label={t(isRead ? 'news.markAsUnread' : 'news.markAsRead')}
      className={cn(
        'ms-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors',
        isRead
          ? 'text-app-primary-500 hover:bg-accent'
          : 'text-muted-foreground/70 hover:text-app-primary-500 hover:bg-accent',
      )}
    >
      {isRead ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
    </button>
  )
}

function timeAgo(iso, locale) {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const diff = Date.now() - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return locale === 'ar' ? 'الآن' : 'now'
  if (mins < 60) return locale === 'ar' ? `${mins}د` : `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return locale === 'ar' ? `${hours}س` : `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return locale === 'ar' ? `${days}ي` : `${days}d`
  return new Date(then).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-CA')
}

function formatCount(n) {
  if (!Number.isFinite(n)) return ''
  const abs = Math.abs(n)
  if (abs < 1000) return String(n)
  if (abs < 10000) {
    const k = n / 1000
    return `${k.toFixed(1).replace(/\.0$/, '')}K`
  }
  if (abs < 1000000) return `${Math.round(n / 1000)}K`
  if (abs < 10000000) {
    const m = n / 1000000
    return `${m.toFixed(1).replace(/\.0$/, '')}M`
  }
  if (abs < 1000000000) return `${Math.round(n / 1000000)}M`
  const b = n / 1000000000
  return `${b.toFixed(1).replace(/\.0$/, '')}B`
}

function articleIsArabic(text) {
  if (!text) return false
  let arab = 0
  let latin = 0
  for (const ch of text) {
    const code = ch.codePointAt(0)
    if (!code) continue
    if (code >= 0x0600 && code <= 0x06FF) arab += 1
    else if ((code >= 0x0041 && code <= 0x005A) || (code >= 0x0061 && code <= 0x007A)) latin += 1
  }
  return arab > latin
}

export default RedditCard
